import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getCompanyNames } from "../services/companyService";
import { getFunctionsByCompany } from "../services/functionService";
import { getJobCategories } from "../services/jobCategoryService";
import { getJobStatuses } from "../services/jobStatusService";
import {
  createJob,
  updateJob,
  getJobsByCompanyId,
  getJobWithRelations,
  generateJobDescription,
} from "../services/jobService";
import {
  getJobHierarchyFlat,
  reparentJob,
  bulkReparentJobs,
} from "../services/jobHierarchyService";
import { getTasksByCompanyId } from "../services/taskService";
import { toast } from "../utils/toast";
import CurrencySelector from "../components/ui/CurrencySelector";
import { getCurrencySymbol } from "../services/currencyService";
import { LEVELS } from "../components/ui/levels";
import SideTabs from "../components/layout/SideTabs";
import SkillTable from "../components/ui/SkillTable";
import SkillAddModal from "../components/ui/SkillAddModal";
import RaciMatrix from "../components/ui/RaciMatrix";
import RichTextEditor from "../components/ui/RichTextEditor";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import JobCombobox from "./c-level-org-chart/JobCombobox";
import OrgSearch from "./c-level-org-chart/OrgSearch";
import UnassignedPanel from "./c-level-org-chart/UnassignedPanel";
import {
  CHILD_PAGE_SIZE,
  NODE_H,
  NODE_W,
  allExpandable,
  boundsOf,
  buildEdgePath,
  buildHighlightPath,
  buildMinimap,
  buildOrgIndex,
  buildSearchIndex,
  buildTeamRects,
  childLimitFor,
  computeInitialExpanded,
  computeTreeLayout,
  countInSubtrees,
  cullItems,
  formatCount,
  functionHeads,
  functionKeyOf,
  getAncestors,
  getDescendants,
  mergeJobOptions,
  moreKey,
  toJobOptions,
} from "./c-level-org-chart/orgChartModel";
import "../styles/job-org-chart.css";

// ─── constants ────────────────────────────────────────────────────────────────
const MIN_SCALE = 0.2;
const MAX_SCALE = 2;
// Below this zoom, cards drop their detail and show a large title instead.
const COMPACT_SCALE = 0.5;
// Nodes are culled against the viewport, grown by an overscan margin and
// snapped to a grid so panning within a cell does not re-render the layers.
const CULL_CELL_PX = 400;
const CULL_OVERSCAN_PX = 320;
// Used when the canvas has not been measured (e.g. no ResizeObserver).
const FALLBACK_W = 1200;
const FALLBACK_H = 800;
const PANEL_W = 280;
// Narrower canvases than this can't share their width with the unassigned
// panel, so it overlays the chart there instead (see job-org-chart.css).
const PANEL_SHARE_MIN_W = PANEL_W + 320;
// A one-finger touch has to travel this far before it pans, so taps still
// select cards and press their buttons.
const TOUCH_SLOP_PX = 8;
// Touches that start on these keep their own behaviour (scrolling the panel,
// pressing buttons, typing) instead of panning or zooming the chart.
const TOUCH_IGNORE_SELECTOR =
  ".joh-unassigned-panel, .joh-unassigned-panel-trigger, button, input, select, textarea, a";
// Phones start with the unassigned panel closed: open, it would cover the chart.
const PHONE_QUERY = "(max-width: 640px)";
const isPhoneViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(PHONE_QUERY).matches;
const MINIMAP_W = 160;
const MINIMAP_H = 110;
const FLASH_MS = 1800;
const ENTER_ANIMATION_MS = 650;

const AVATAR_PALETTE = [
  ["#3b82f6", "#1d4ed8"], // blue
  ["#8b5cf6", "#6d28d9"], // violet
  ["#06b6d4", "#0e7490"], // cyan
  ["#f59e0b", "#d97706"], // amber
  ["#10b981", "#059669"], // emerald
  ["#ec4899", "#db2777"], // pink
  ["#f97316", "#ea580c"], // orange
];

function avatarGradient(str = "") {
  const idx = Math.abs(
    [...str].reduce((a, c) => a + c.charCodeAt(0), 0),
  ) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}

function initials(name = "") {
  const parts = String(name || "").trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const raf = (cb) =>
  typeof window.requestAnimationFrame === "function" ?
    window.requestAnimationFrame(cb)
  : window.setTimeout(cb, 16);
const cancelRaf = (id) =>
  typeof window.cancelAnimationFrame === "function" ?
    window.cancelAnimationFrame(id)
  : window.clearTimeout(id);

// A callback with a stable identity that always runs the latest closure, so
// memoized chart layers are not re-rendered just because a handler changed.
function useStableHandler(fn) {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args) => ref.current(...args), []);
}

// ─── Job form modal (tabbed — matches Job Management page) ──────────────────────
const EMPTY_FORM = {
  name: "",
  jobCode: "",
  description: "",
  employeeName: "",
  function_id: "",
  category_id: "",
  status_id: "",
  jobLevel: "",
  hourlyRate: "",
  currencyType: "USD",
  maxHoursPerDay: "",
  staff_count: "",
  daysPerWeek: "5",
  capacityBuffer: "10",
  parent_job_id: "",
  skills: [],
};

function JobFormModal({
  open,
  mode,            // 'create' | 'edit'
  initialData,
  functions,
  categories,
  statuses,
  allTasks,        // tasks for RACI tab
  jobOptions,      // all jobs in company for parent picker (edit mode)
  nodeId,          // id of the node being edited (excluded from parent list)
  onSave,
  onClose,
  saving,
  error,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [raciRows, setRaciRows] = useState([]);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);

  // Reset form + raci when modal opens
  useEffect(() => {
    if (open) {
      const d = initialData;
      setForm(
        d ?
          {
            name:           d.name           || "",
            jobCode:        d.jobCode         || "",
            description:    d.description     || "",
            employeeName:   d.employeeName    || "",
            function_id:    d.function_id     ? String(d.function_id)  : "",
            category_id:    d.category_id    ? String(d.category_id)  : "",
            status_id:      d.status_id      ? String(d.status_id)     : "",
            jobLevel:       d.jobLevel        || "",
            hourlyRate:     d.hourlyRate      != null ? String(d.hourlyRate)     : "",
            currencyType:   d.currencyType    || "USD",
            maxHoursPerDay: d.maxHoursPerDay != null ? String(d.maxHoursPerDay) : "",
            staff_count:    d.staff_count    != null ? String(d.staff_count)    : "",
            daysPerWeek:    d.daysPerWeek    != null ? String(d.daysPerWeek)    : "5",
            capacityBuffer: d.capacityBuffer != null ? String(d.capacityBuffer) : "10",
            parent_job_id:  d.parentId       ? String(d.parentId) : "",
            skills:         Array.isArray(d.skills) ? d.skills : [],
          }
        : { ...EMPTY_FORM },
      );
      setRaciRows(Array.isArray(initialData?.raciRows) ? initialData.raciRows : []);
      setShowAddSkill(false);
    }
  }, [open, initialData]);

  if (!open) return null;

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setVal = (field, val) => setForm((p) => ({ ...p, [field]: val }));
  const selectedLevel = LEVELS.find((l) => l.name === form.jobLevel);

  const handleGenerateDescription = async () => {
    if (generatingDesc) return;
    const taskPayload = raciRows
      .map((row) => {
        const task = (allTasks || []).find((t) => String(t.task_id) === String(row.task_id));
        if (!task) return null;
        return {
          taskName: task.task_name || task.name || "",
          taskOverview: task.task_overview || task.overview || "",
          role: row.role || null,
        };
      })
      .filter(Boolean);
    if (taskPayload.length === 0) {
      toast.error("No tasks assigned in the RACI tab yet. Add tasks first, then generate a description.");
      return;
    }
    try {
      setGeneratingDesc(true);
      const description = await generateJobDescription({ jobName: form.name || undefined, tasks: taskPayload });
      setVal("description", description);
      toast.success("Description generated! Review and edit it before saving.");
    } catch (e) {
      toast.error(e?.message || "Failed to generate description");
    } finally {
      setGeneratingDesc(false);
    }
  };

  // Collect full form data (including skills/raci) for save
  const getFormData = () => ({ ...form, raciRows });

  // ── Tab contents ────────────────────────────────────────────────
  const basicDetailsTab = (
    <div className="role-card">
      <div className="form-grid">
        <div className="form-group">
          <label>Job Name *</label>
          <input
            value={form.name}
            onChange={(e) => setVal("name", e.target.value)}
            placeholder="e.g. Senior Software Engineer"
            autoFocus
          />
        </div>
        <div className="form-group">
          <label>Job Code *</label>
          <input
            value={form.jobCode}
            onChange={(e) => setVal("jobCode", e.target.value)}
            placeholder="e.g. JOB-001"
          />
        </div>
        <div className="form-group">
          <label>Function *</label>
          <select value={form.function_id} onChange={set("function_id")}>
            <option value="">Select Function</option>
            {(functions || []).map((f) => (
              <option key={f.function_id} value={f.function_id}>{f.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Hourly Rate</label>
          <div className="currency-input-group">
            <span className="currency-input-symbol">{getCurrencySymbol(form.currencyType)}</span>
            <input
              className="currency-input-field"
              type="number"
              min={0}
              step={0.01}
              value={form.hourlyRate}
              onChange={set("hourlyRate")}
              placeholder="e.g. 25"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Currency</label>
          <CurrencySelector value={form.currencyType} onChange={(code) => setVal("currencyType", code)} />
        </div>
        <div className="form-group">
          <label>Max Hours Per Day</label>
          <input
            type="number" min={1} max={8} step={1}
            value={form.maxHoursPerDay}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Math.max(1, Math.min(8, Number(e.target.value)));
              setVal("maxHoursPerDay", v);
            }}
            placeholder="e.g. 8"
          />
          <small className="hint">Allowed range: 1 to 8 hours</small>
        </div>
        <div className="form-group">
          <label>Staff Count</label>
          <input
            id="job-staff-count" type="number" min={1} step={1}
            value={form.staff_count}
            onChange={(e) => setVal("staff_count", e.target.value)}
            placeholder="e.g. 3"
          />
          <small className="hint">How many people hold this job.</small>
        </div>
        <div className="form-group">
          <label>Days Per Week</label>
          <input
            type="number" min={1} max={6} step={1}
            value={form.daysPerWeek}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Math.max(1, Math.min(6, Number(e.target.value)));
              setVal("daysPerWeek", v);
            }}
            placeholder="e.g. 5"
          />
          <small className="hint">Allowed range: 1 to 6 days</small>
        </div>
        <div className="form-group">
          <label title="Share of the workday held back for informal work">Capacity Buffer (%)</label>
          <input
            type="number" min={0} max={100} step={1}
            value={form.capacityBuffer}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Math.max(0, Math.min(100, Number(e.target.value)));
              setVal("capacityBuffer", v);
            }}
            placeholder="e.g. 10"
          />
          <small className="hint">Portion of available hours held back for informal work — meetings, admin, breaks</small>
        </div>
        <div className="form-group">
          <label>Job Category *</label>
          <select value={form.category_id} onChange={set("category_id")}>
            <option value="">Select job category</option>
            {(categories || []).map((c) => (
              <option key={c.id ?? c.job_category_id} value={c.id ?? c.job_category_id}>
                {c.description || c.name || `Category ${c.id ?? c.job_category_id}`}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Job Status *</label>
          <select value={form.status_id} onChange={set("status_id")}>
            <option value="">Select job status</option>
            {(statuses || []).map((s) => (
              <option key={s.id ?? s.job_status_id} value={s.id ?? s.job_status_id}>
                {s.description || s.name || `Status ${s.id ?? s.job_status_id}`}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Level Name *</label>
          <select value={form.jobLevel} onChange={set("jobLevel")}>
            <option value="">Select Level Name</option>
            {LEVELS.map((l) => (
              <option key={l.name} value={l.name}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Level Rank</label>
          <select
            value={selectedLevel?.rank || ""}
            onChange={(e) => setVal("jobLevel", LEVELS.find((l) => l.rank === Number(e.target.value))?.name || "")}
          >
            <option value="">Select Rank</option>
            {LEVELS.map((l) => (
              <option key={l.rank} value={l.rank}>{l.rank}</option>
            ))}
          </select>
        </div>
        <div className="form-group full">
          <label>Level Description</label>
          <input disabled value={selectedLevel?.description || ""} />
        </div>
        {/* Parent Job — edit mode only */}
        {mode === "edit" && (
          <div className="form-group">
            <label htmlFor="job-parent-job">Parent Job</label>
            <JobCombobox
              id="job-parent-job"
              value={form.parent_job_id || ""}
              options={jobOptions || []}
              excludeValue={nodeId}
              noneLabel="— No Parent (Root Job) —"
              placeholder="— No Parent (Root Job) —"
              onChange={(val) => setVal("parent_job_id", val)}
            />
            <small className="hint">The job this position directly reports to in the hierarchy.</small>
          </div>
        )}
      </div>
    </div>
  );

  const skillsTab = (
    <div className="role-card">
      <div
        className="section-title"
        style={{ marginBottom: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span>Skills</span>
        <button
          type="button"
          className="primary-btn icon-square"
          onClick={() => setShowAddSkill(true)}
          aria-label="Add Skill"
          title="Add Skill"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <SkillTable
        mode="manage"
        skills={form.skills}
        levels={LEVELS}
        onDelete={(idx) => setForm((prev) => ({ ...prev, skills: prev.skills.filter((_, i) => i !== idx) }))}
      />
      <SkillAddModal
        open={showAddSkill}
        onClose={() => setShowAddSkill(false)}
        levels={LEVELS}
        onSave={(s) => {
          setForm((prev) => ({ ...prev, skills: [...prev.skills, s] }));
          setShowAddSkill(false);
        }}
      />
    </div>
  );

  const raciTab = (
    <RaciMatrix
      mode="manage"
      entityType="task"
      tasks={allTasks || []}
      rows={raciRows}
      onChange={setRaciRows}
      disabled={mode === "create"}
      disabledMessage={mode === "create"
        ? "Save this job first, then link tasks and assign RACI roles from the job's edit page."
        : undefined}
    />
  );

  const descriptionTab = (
    <div className="role-card">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          type="button"
          className="secondary-btn"
          onClick={handleGenerateDescription}
          disabled={generatingDesc}
          title={raciRows.length === 0 ? "Assign tasks in the RACI tab first" : "Generate a description from your assigned RACI tasks"}
        >
          {generatingDesc ? "Generating..." : "✨ Generate Description"}
        </button>
      </div>
      <RichTextEditor
        label="Job Description"
        value={form.description}
        onChange={(val) => setVal("description", val)}
        height={280}
      />
      {raciRows.length === 0 && (
        <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 8 }}>
          Tip: Assign tasks in the RACI tab first, then come back here to auto-generate the description.
        </p>
      )}
    </div>
  );

  return (
    <div
      className="joh-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="joh-modal joh-modal-wide"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="joh-modal-header">
          <div className="joh-modal-header-left">
            <div className="joh-modal-icon">
              {mode === "edit" ? "✏️" : "➕"}
            </div>
            <div>
              <h2 className="joh-modal-title">
                {mode === "edit" ? "Edit Job" : "Create New Job"}
              </h2>
              <p className="joh-modal-subtitle">
                {mode === "edit" ? "Update position details" : "Add a new position to the hierarchy"}
              </p>
            </div>
          </div>
          <button id="joh-modal-close-btn" className="joh-modal-close" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="joh-form-error" style={{ margin: "0 0 0 0", borderRadius: 0 }}>
            <span>⚠</span> {error}
          </div>
        )}

        {/* Body with SideTabs */}
        <div className="joh-modal-body joh-modal-body-tabs">
          <SideTabs
            defaultActiveId="basic"
            tabs={[
              { id: "basic",       label: "Basic Details", content: basicDetailsTab },
              { id: "skills",      label: "Skills",         content: skillsTab },
              { id: "raci",        label: "RACI",           content: raciTab },
              { id: "description", label: "Description",    content: descriptionTab },
            ]}
          />
        </div>

        {/* Footer */}
        <div className="joh-modal-footer">
          <button
            id="joh-modal-cancel-btn"
            className="joh-modal-btn ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            id="joh-modal-save-btn"
            className="joh-modal-btn primary"
            onClick={() => onSave(getFormData())}
            disabled={saving}
          >
            {saving ? (
              <><span className="joh-saving-dot" /> Saving…</>
            ) : (
              mode === "edit" ? "Save Changes" : "Create Job"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── Single node card ─────────────────────────────────────────────────────────
const NodeCard = memo(function NodeCard({
  node,
  selected,
  expanded,
  hasChildren,
  hiddenCount,
  focusCount,     // descendants in the highlighted function, or null
  focusName,
  dimmed,
  fnMatch,
  onPath,
  flash,
  compact,
  onSelect,
  onDoubleClick,
  onAddChild,
  onAddSibling,
  onUnassign,
  onToggleCollapse,
  isRoot,
}) {
  const [g1, g2] = avatarGradient(node.name);
  const collapsed = hasChildren && !expanded;
  const className = [
    "joh-node-card",
    selected && "selected",
    compact && "compact",
    dimmed && "dimmed",
    fnMatch && "fn-match",
    onPath && "on-path",
    flash && "flash",
  ].filter(Boolean).join(" ");

  let toggleTitle = collapsed ? `Expand — ${formatCount(hiddenCount)} positions below` : "Collapse children";
  if (collapsed && focusCount != null) {
    toggleTitle = `Expand — ${formatCount(focusCount)} of ${formatCount(hiddenCount)} positions below are in ${focusName}`;
  }

  return (
    <div
      id={`joh-node-${node.id}`}
      className={className}
      onClick={(e) => { e.stopPropagation(); onSelect(node.id); }}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(node); }}
      title={`${node.name || "Untitled"}${node.functionName ? ` · ${node.functionName}` : ""} — double-click to edit`}
    >
      {compact ? (
        <div className="joh-node-compact" style={{ borderTopColor: g1 }}>
          <div className="joh-node-compact-title">{node.name || "Untitled"}</div>
        </div>
      ) : (
        <div className="joh-node-inner">
          <div className="joh-node-avatar-row">
            <div
              className="joh-node-avatar"
              style={{ background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
            >
              {initials(node.name)}
            </div>
            <div className="joh-node-title-col">
              <div className="joh-node-title">{node.name || "Untitled"}</div>
              {node.employeeName ? (
                <div className="joh-node-employee" title={node.employeeName}>{node.employeeName}</div>
              ) : (
                <div className="joh-node-vacant">— Vacant —</div>
              )}
            </div>
          </div>
          {node.functionName && (
            <div className="joh-node-dept" title={node.functionName}>
              📁 {node.functionName}
            </div>
          )}
        </div>
      )}

      {/* Sibling left */}
      {!isRoot && (
        <button
          id={`joh-add-sibling-left-${node.id}`}
          className="joh-action-sibling left"
          title="Add sibling position"
          onClick={(e) => { e.stopPropagation(); onAddSibling(node.id); }}
        >+</button>
      )}
      {/* Sibling right */}
      {!isRoot && (
        <button
          id={`joh-add-sibling-right-${node.id}`}
          className="joh-action-sibling right"
          title="Add sibling position"
          onClick={(e) => { e.stopPropagation(); onAddSibling(node.id); }}
        >+</button>
      )}
      {/* Child */}
      <button
        id={`joh-add-child-${node.id}`}
        className="joh-action-child"
        title="Add child position"
        onClick={(e) => { e.stopPropagation(); onAddChild(node.id); }}
      >+</button>
      {/* Unassign */}
      <button
        id={`joh-unassign-${node.id}`}
        className="joh-action-delete"
        title="Remove from hierarchy (move to unassigned)"
        onClick={(e) => { e.stopPropagation(); onUnassign(node.id); }}
      >✕</button>

      {/* Collapse / expand toggle — collapsed nodes show how much sits below */}
      {hasChildren && (
        <button
          id={`joh-toggle-${node.id}`}
          className={`joh-action-toggle${collapsed ? " collapsed" : ""}${collapsed && focusCount > 0 ? " has-focus" : ""}`}
          title={toggleTitle}
          aria-expanded={!collapsed}
          onClick={(e) => { e.stopPropagation(); onToggleCollapse(node.id); }}
        >
          {collapsed ? (
            <>
              <span aria-hidden="true">▸</span>
              {focusCount != null ? `${formatCount(focusCount)} / ${formatCount(hiddenCount)}` : formatCount(hiddenCount)}
            </>
          ) : "▾"}
        </button>
      )}
    </div>
  );
});

// ─── "More positions" card (children beyond the current page) ────────────────
const MoreCard = memo(function MoreCard({ parentId, hidden, x, y, compact, onShowMore, onShowAll }) {
  const step = Math.min(CHILD_PAGE_SIZE, hidden);
  return (
    <div className="joh-node" style={{ left: x, top: y }}>
      <div
        className={`joh-more-card${compact ? " compact" : ""}`}
        onClick={(e) => { e.stopPropagation(); if (compact) onShowMore(parentId); }}
        title={`${formatCount(hidden)} more positions report here`}
      >
        <div className="joh-more-count">+{formatCount(hidden)}</div>
        <div className="joh-more-label">more position{hidden === 1 ? "" : "s"}</div>
        {!compact && (
          <div className="joh-more-actions">
            <button type="button" onClick={(e) => { e.stopPropagation(); onShowMore(parentId); }}>
              Show {formatCount(step)} more
            </button>
            {hidden > step && (
              <button type="button" className="ghost" onClick={(e) => { e.stopPropagation(); onShowAll(parentId); }}>
                Show all
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Positioned node (drop target + card) ─────────────────────────────────────
const OrgNode = memo(function OrgNode({ x, y, node, isDropTarget, animate, handlers, ...card }) {
  return (
    <div
      className={`joh-node${animate ? " animating-in" : ""}`}
      style={{ left: x, top: y }}
      onDragOver={(e) => handlers.onNodeDragOver(e, node.id)}
      onDragLeave={handlers.onNodeDragLeave}
      onDrop={(e) => handlers.onNodeDrop(e, node.id)}
    >
      <div
        className={isDropTarget ? "joh-drop-target-ring" : ""}
        style={{ borderRadius: 12, transition: "box-shadow 0.15s, border-color 0.15s" }}
      >
        <NodeCard
          node={node}
          {...card}
          onSelect={handlers.onSelect}
          onDoubleClick={handlers.onEdit}
          onAddChild={handlers.onAddChild}
          onAddSibling={handlers.onAddSibling}
          onUnassign={handlers.onUnassign}
          onToggleCollapse={handlers.onToggle}
        />
      </div>
    </div>
  );
});

// Only the cards inside the (overscanned) viewport are mounted.
const NodeLayer = memo(function NodeLayer({
  items,
  index,
  expanded,
  selectedId,
  highlightId,
  pathIds,
  dropTargetId,
  focus,
  flashId,
  animateAll,
  animateParent,
  compact,
  handlers,
}) {
  return (
    <div className="joh-nodes-layer">
      {items.map((it) => {
        if (it.kind === "more") {
          return (
            <MoreCard
              key={it.key}
              parentId={it.parentId}
              hidden={it.hidden}
              x={it.x}
              y={it.y}
              compact={compact}
              onShowMore={handlers.onShowMore}
              onShowAll={handlers.onShowAll}
            />
          );
        }
        const node = index.byId.get(it.id);
        const children = index.childrenOf.get(it.id);
        const inFunction = focus ? functionKeyOf(node) === focus.key : false;
        const below = focus ? focus.counts.get(it.id) || 0 : null;
        return (
          <OrgNode
            key={it.key}
            x={it.x}
            y={it.y}
            node={node}
            handlers={handlers}
            isDropTarget={dropTargetId === it.id}
            animate={animateAll || (animateParent != null && it.parentId === animateParent)}
            selected={selectedId === it.id || highlightId === it.id}
            expanded={expanded.has(it.id)}
            hasChildren={Boolean(children && children.length)}
            hiddenCount={index.descendantCount.get(it.id) || 0}
            focusCount={below}
            focusName={focus ? focus.name : null}
            dimmed={Boolean(focus) && !inFunction && below === 0}
            fnMatch={inFunction}
            onPath={pathIds.has(it.id)}
            flash={flashId === it.id}
            compact={compact}
            isRoot={it.id === index.mainRootId || !node.parentId}
          />
        );
      })}
    </div>
  );
});

// Connectors and team blocks in view; connectors are one path (plus one for
// the selection).
const EdgeLayer = memo(function EdgeLayer({ rect, d, highlight, teams }) {
  const w = rect.x1 - rect.x0;
  const h = rect.y1 - rect.y0;
  return (
    <svg
      className="joh-svg-layer"
      style={{ left: rect.x0, top: rect.y0, pointerEvents: "none" }}
      width={w}
      height={h}
      viewBox={`${rect.x0} ${rect.y0} ${w} ${h}`}
    >
      {teams.map((t) => (
        <g key={t.key}>
          <rect className="joh-team-block" x={t.x} y={t.y} width={t.w} height={t.h} rx="16" ry="16" />
          <text className="joh-team-label" x={t.x + 14} y={t.y - 8}>
            {formatCount(t.count)} direct reports
          </text>
        </g>
      ))}
      {d && <path d={d} className="joh-connector" />}
      {highlight && <path d={highlight} className="joh-connector highlighted" />}
    </svg>
  );
});

const Minimap = memo(function Minimap({ data, selectedPos, view, viewportW, viewportH }) {
  if (!data) return null;
  const { scale, ox, oy } = data;
  const vx = (-view.x / view.scale) * scale + ox;
  const vy = (-view.y / view.scale) * scale + oy;
  const vw = (viewportW / view.scale) * scale;
  const vh = (viewportH / view.scale) * scale;
  const sw = Math.max(2, NODE_W * scale);
  const sh = Math.max(2, NODE_H * scale);
  return (
    <div className="joh-minimap">
      <svg width={MINIMAP_W} height={MINIMAP_H} style={{ overflow: "hidden", display: "block" }}>
        <path d={data.edges} fill="none" stroke="#cbd5e1" strokeWidth="0.8" />
        <path d={data.nodes} fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.6" />
        {selectedPos && (
          <rect
            x={selectedPos.x * scale + ox - sw / 2}
            y={selectedPos.y * scale + oy}
            width={sw}
            height={sh}
            rx="1"
            fill="#3b82f6"
            stroke="#2563eb"
            strokeWidth="0.6"
          />
        )}
        {/* Viewport Finder Box */}
        <rect
          x={vx}
          y={vy}
          width={vw}
          height={vh}
          fill="rgba(59, 130, 246, 0.08)"
          stroke="#3b82f6"
          strokeWidth="1"
          strokeDasharray="2,2"
          rx="1"
          ry="1"
        />
      </svg>
    </div>
  );
});

// ─── Assign-Parent modal (for unassigned cards) ──────────────────────────────
function AssignParentModal({ open, node, jobOptions, nodeId, onSave, onClose, saving, error }) {
  const [parentId, setParentId] = useState("");
  useEffect(() => { if (open) setParentId(""); }, [open]);
  if (!open || !node) return null;
  return (
    <div
      className="joh-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="joh-modal" role="dialog" aria-modal="true" style={{ maxWidth: 460 }}>
        <div className="joh-modal-header">
          <div className="joh-modal-header-left">
            <div className="joh-modal-icon">🔗</div>
            <div>
              <h2 className="joh-modal-title">Assign Parent Position</h2>
              <p className="joh-modal-subtitle">Choose where "<strong>{node.name}</strong>" reports to</p>
            </div>
          </div>
          <button className="joh-modal-close" onClick={onClose}>✕</button>
        </div>
        {error && (
          <div className="joh-form-error" style={{ margin: "0", borderRadius: 0 }}>
            <span>⚠</span> {error}
          </div>
        )}
        <div className="joh-modal-body">
          <div className="form-group">
            <label htmlFor="joh-assign-parent-job">Parent Position *</label>
            <JobCombobox
              id="joh-assign-parent-job"
              value={parentId}
              options={jobOptions || []}
              excludeValue={nodeId}
              placeholder="Search for the parent job…"
              onChange={setParentId}
            />
            <small className="hint" style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, display: "block" }}>
              The job this position will directly report to in the hierarchy.
            </small>
          </div>
        </div>
        <div className="joh-modal-footer">
          <button className="joh-modal-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="joh-modal-btn primary"
            disabled={saving || !parentId}
            onClick={() => onSave(parentId)}
          >
            {saving ? <><span className="joh-saving-dot" /> Assigning…</> : "Assign Parent"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Unassign confirmation modal (custom popup modal) ─────────────────────────
function UnassignConfirmModal({ open, jobName, hasChildren, onConfirm, onCancel, processing }) {
  if (!open) return null;
  return (
    <div className="joh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="joh-confirm-modal" role="alertdialog" aria-modal="true" style={{ maxWidth: 400 }}>
        <div className="joh-confirm-icon" style={{ backgroundColor: "#eff6ff", color: "#3b82f6", fontSize: "1.8rem" }}>🔗</div>
        <h2 className="joh-confirm-title" style={{ marginTop: 6, fontSize: "1.1rem" }}>Remove position?</h2>
        <p className="joh-confirm-text" style={{ fontSize: "0.85rem", lineHeight: 1.5, color: "#64748b" }}>
          {hasChildren ? (
            <>
              Are you sure you want to remove "<strong>{jobName}</strong>" and <strong>all of its sub-positions</strong> from the hierarchy? They will all be moved to the unassigned positions panel.
            </>
          ) : (
            <>
              Are you sure you want to remove "<strong>{jobName}</strong>" from the hierarchy? It will be moved to the unassigned positions panel.
            </>
          )}
        </p>
        <div className="joh-confirm-actions" style={{ gap: 12, marginTop: 8 }}>
          <button
            id="joh-unassign-cancel-btn"
            className="joh-modal-btn ghost"
            onClick={onCancel}
            disabled={processing}
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            id="joh-unassign-confirm-btn"
            className="joh-modal-btn primary"
            onClick={onConfirm}
            disabled={processing}
            style={{ flex: 1 }}
          >
            {processing ? <><span className="joh-saving-dot" /> Removing…</> : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Choice Modal (Add New vs Existing) ───────────────────────────────────────
function ChoiceModal({ open, onNewJob, onExistingJob, onClose }) {
  if (!open) return null;
  return (
    <div className="joh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="joh-modal" role="dialog" aria-modal="true" style={{ maxWidth: 400, textAlign: 'center' }}>
        <div className="joh-modal-header">
          <div className="joh-modal-header-left">
            <h2 className="joh-modal-title">Add Position</h2>
          </div>
          <button className="joh-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="joh-modal-body" style={{ padding: '24px 20px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button className="joh-modal-btn primary" onClick={onNewJob} style={{ flex: 1 }}>➕ New Job</button>
          <button className="joh-modal-btn secondary" onClick={onExistingJob} style={{ flex: 1 }}>🔗 Existing Job</button>
        </div>
      </div>
    </div>
  );
}

// ─── Existing Job Modal ───────────────────────────────────────────────────────
function ExistingJobModal({ open, parentId, unassignedJobs, onAssign, onClose, saving, error }) {
  const [selectedJobId, setSelectedJobId] = useState("");
  const [parentJobId, setParentJobId] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedJobId("");
      const firstId = unassignedJobs && unassignedJobs.length > 0 ? String(unassignedJobs[0].id ?? unassignedJobs[0].job_id ?? "") : "";
      setParentJobId(firstId);
    }
  }, [open, unassignedJobs]);

  const jobOptions = useMemo(() => toJobOptions(unassignedJobs), [unassignedJobs]);

  if (!open) return null;

  const hasNoJobs = !unassignedJobs || unassignedJobs.length === 0;
  const isRootSelection = parentId === null;
  const childCount = isRootSelection && parentJobId ? jobOptions.length - 1 : jobOptions.length;

  const canSubmit = isRootSelection
    ? (parentJobId && (childCount === 0 || selectedJobId))
    : selectedJobId;

  return (
    <div className="joh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="joh-modal" role="dialog" aria-modal="true" style={{ maxWidth: 460 }}>
        <div className="joh-modal-header">
          <div className="joh-modal-header-left">
            <div className="joh-modal-icon">🔗</div>
            <div>
              <h2 className="joh-modal-title">Select Existing Job</h2>
              <p className="joh-modal-subtitle">
                {isRootSelection
                  ? "Select a parent and child position to build the hierarchy"
                  : "Choose a job from the unassigned list"}
              </p>
            </div>
          </div>
          <button className="joh-modal-close" onClick={onClose}>✕</button>
        </div>
        {error && (
          <div className="joh-form-error" style={{ margin: "0", borderRadius: 0 }}>
            <span>⚠</span> {error}
          </div>
        )}
        <div className="joh-modal-body">
          {hasNoJobs ? (
            <div style={{ textAlign: "center", padding: "28px 16px", color: "#64748b" }}>
              <div style={{ fontSize: "2.2rem", marginBottom: "10px" }}>📭</div>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", marginBottom: "6px" }}>
                No existing jobs in this company
              </div>
              <div style={{ fontSize: "0.875rem", lineHeight: 1.5, color: "#64748b" }}>
                There are currently no unassigned jobs available in this company. Please create a new job first.
              </div>
            </div>
          ) : (
            <>
              {isRootSelection && (
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label htmlFor="joh-existing-parent-job">Parent / Root Position *</label>
                  <JobCombobox
                    id="joh-existing-parent-job"
                    value={parentJobId}
                    options={jobOptions}
                    placeholder="Search for the parent position…"
                    onChange={(val) => {
                      setParentJobId(val);
                      setSelectedJobId("");
                    }}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="joh-existing-child-job">{isRootSelection ? "Child Position" : "Unassigned Jobs *"}</label>
                <JobCombobox
                  id="joh-existing-child-job"
                  value={selectedJobId}
                  options={jobOptions}
                  excludeValue={isRootSelection ? parentJobId : undefined}
                  placeholder={isRootSelection ? "Search for a child position…" : "Search unassigned jobs…"}
                  disabled={childCount === 0}
                  onChange={setSelectedJobId}
                />
                {isRootSelection && childCount === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "6px" }}>
                    ℹ No other unassigned existing jobs available to attach as a child.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="joh-modal-footer">
          <button className="joh-modal-btn ghost" onClick={onClose} disabled={saving}>
            {hasNoJobs ? "Close" : "Cancel"}
          </button>
          {!hasNoJobs && (
            <button
              className="joh-modal-btn primary"
              disabled={saving || !canSubmit}
              onClick={() => onAssign(selectedJobId, isRootSelection ? parentJobId : parentId)}
            >
              {saving ? <><span className="joh-saving-dot" /> Assigning…</> : "Assign Job"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Canvas (pan + zoom) ──────────────────────────────────────────────────────
function OrgCanvas({
  nodes,
  designatedRootId,
  onClearDesignatedRoot,
  selectedId,
  functions,
  categories,
  statuses,
  allTasks,
  companyId,
  onReload,
  onSelectNode,
}) {
  const wrapperRef = useRef(null);

  // Pan / zoom state
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [panning, setPanning] = useState(false);
  const panRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Structure of the whole company, rebuilt only when the data changes
  const index = useMemo(() => buildOrgIndex(nodes, designatedRootId), [nodes, designatedRootId]);

  // Progressive disclosure: which nodes show their children, how many
  // children each shows, and jobs pinned into view by search.
  const [disclosure, setDisclosure] = useState(() => ({
    rootId: index.mainRootId,
    expanded: computeInitialExpanded(index),
  }));
  const expanded = disclosure.expanded;
  const [childLimit, setChildLimit] = useState(() => new Map());
  const [pinned, setPinned] = useState(() => new Set());

  // View changes that need the next layout (fit, center, keep-anchored)
  const pendingView = useRef({ kind: "fit", initial: true });
  const [viewTick, setViewTick] = useState(0);
  const requestView = useCallback((req) => {
    pendingView.current = req;
    setViewTick((t) => t + 1);
  }, []);

  // A different root (company data changed underneath) starts a fresh overview.
  useLayoutEffect(() => {
    if (disclosure.rootId === index.mainRootId) return;
    setDisclosure({ rootId: index.mainRootId, expanded: computeInitialExpanded(index) });
    setChildLimit(new Map());
    setPinned(new Set());
    requestView({ kind: "fit", initial: true });
  }, [index, disclosure.rootId, requestView]);

  // All jobs in this company — for the Parent Job pickers. Fetched once; names
  // and newly created jobs are refreshed from the hierarchy on every reload.
  const [companyJobs, setCompanyJobs] = useState([]);
  useEffect(() => {
    if (!companyId) { setCompanyJobs([]); return undefined; }
    let cancelled = false;
    getJobsByCompanyId(companyId).then((jobs) => {
      if (!cancelled) setCompanyJobs(Array.isArray(jobs) ? jobs : []);
    }).catch(() => { if (!cancelled) setCompanyJobs([]); });
    return () => { cancelled = true; };
  }, [companyId]);
  const jobOptions = useMemo(() => mergeJobOptions(companyJobs, nodes), [companyJobs, nodes]);

  // Modals
  const [formModal, setFormModal] = useState({
    open: false, mode: "create", initialData: null, parentId: null, nodeId: null, saving: false, error: "",
  });
  const [assignModal, setAssignModal] = useState({
    open: false, node: null, saving: false, error: "",
  });
  const [unassignModal, setUnassignModal] = useState({
    open: false, nodeId: null, jobName: "", processing: false,
  });
  const [choiceModal, setChoiceModal] = useState({
    open: false, parentId: null,
  });
  const [existingJobModal, setExistingJobModal] = useState({
    open: false, parentId: null, saving: false, error: "",
  });

  // Drag-and-drop state
  const [dragNodeId, setDragNodeId] = useState(null);   // id of unassigned card being dragged
  const [dropTargetId, setDropTargetId] = useState(null); // id of canvas node being hovered over
  const dragRef = useRef(null);
  const dragGhostRef = useRef(null);

  // Unassigned panel
  const [unassignedOpen, setUnassignedOpen] = useState(() => !isPhoneViewport());
  const [panelFocus, setPanelFocus] = useState(null);

  // Search / highlight state
  const [functionFocusKey, setFunctionFocusKey] = useState(null);
  const [jobFocusId, setJobFocusId] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const [animateScope, setAnimateScope] = useState({ all: true, parent: null });
  const pendingReveal = useRef(null);

  const searchIndex = useMemo(() => buildSearchIndex(index, functions), [index, functions]);

  // The job highlighted from search (dropped if it is no longer in the data)
  const jobFocus = useMemo(
    () => (jobFocusId ? index.byId.get(jobFocusId) || null : null),
    [index, jobFocusId],
  );
  const clearJobFocus = useCallback(() => setJobFocusId(null), []);
  // Clicking a position moves the highlight to it; clearing the selection
  // (by panning or clicking the background) does not.
  const selectNode = useStableHandler((id) => {
    if (id) setJobFocusId(null);
    onSelectNode(id);
  });

  const focus = useMemo(() => {
    const fn = functionFocusKey ? searchIndex.functionByKey.get(functionFocusKey) : null;
    if (!fn) return null;
    return { key: fn.key, name: fn.name, counts: countInSubtrees(index, new Set(fn.jobIds)) };
  }, [functionFocusKey, searchIndex, index]);

  // ── Layout + viewport ──────────────────────────────────────────────────────
  const layout = useMemo(
    () => computeTreeLayout(index, { expanded, childLimit, pinned }),
    [index, expanded, childLimit, pinned],
  );
  const shownCount = useMemo(
    () => layout.items.reduce((n, it) => n + (it.kind === "node" ? 1 : 0), 0),
    [layout],
  );

  const unassignedCount = index.unassigned.length;
  const panelShown = unassignedOpen && unassignedCount > 0;
  const viewportW = size.w || FALLBACK_W;
  const viewportH = size.h || FALLBACK_H;
  // Width not covered by the unassigned panel, to centre the hierarchy in
  const usableW = panelShown && viewportW >= PANEL_SHARE_MIN_W ? viewportW - PANEL_W : viewportW;

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Visible canvas rectangle, overscanned and snapped to a grid so the memoized
  // layers only change when panning crosses a cell boundary.
  const cell = CULL_CELL_PX / view.scale;
  const overscan = CULL_OVERSCAN_PX / view.scale;
  const rx0 = Math.floor((-view.x / view.scale - overscan) / cell) * cell;
  const ry0 = Math.floor((-view.y / view.scale - overscan) / cell) * cell;
  const rx1 = Math.ceil(((viewportW - view.x) / view.scale + overscan) / cell) * cell;
  const ry1 = Math.ceil(((viewportH - view.y) / view.scale + overscan) / cell) * cell;
  const cullRect = useMemo(() => ({ x0: rx0, y0: ry0, x1: rx1, y1: ry1 }), [rx0, ry0, rx1, ry1]);
  const culledItems = useMemo(() => cullItems(layout.items, cullRect), [layout, cullRect]);
  const edgePath = useMemo(() => buildEdgePath(layout.groups, cullRect), [layout, cullRect]);
  const teamRects = useMemo(() => buildTeamRects(layout.groups, cullRect), [layout, cullRect]);
  // The highlighted reporting line: the selected position, or the job kept
  // from search once a pan has cleared the selection.
  const highlightId = selectedId || (jobFocus && jobFocus.id) || null;
  const highlightPath = useMemo(() => buildHighlightPath(layout, highlightId), [layout, highlightId]);
  const pathIds = useMemo(
    () => new Set(highlightId ? getAncestors(index, highlightId) : []),
    [index, highlightId],
  );
  const minimap = useMemo(() => buildMinimap(layout, MINIMAP_W, MINIMAP_H), [layout]);
  const compact = view.scale < COMPACT_SCALE;

  const canvasW = layout.bounds ? Math.max(layout.bounds.maxX + NODE_W + 60, 800) : 800;
  const canvasH = layout.bounds ? Math.max(layout.bounds.maxY + NODE_H + 40, 600) : 600;

  // Apply pending view requests once the layout they depend on exists.
  useLayoutEffect(() => {
    const req = pendingView.current;
    if (!req) return;
    if (!size.w && typeof ResizeObserver !== "undefined") return; // not measured yet
    pendingView.current = null;
    const vw = usableW;
    const vh = viewportH;

    const fit = (b, minScale, maxScale) => {
      if (!b) return;
      const pad = 40;
      const w = Math.max(1, b.maxX - b.minX);
      const h = Math.max(1, b.maxY - b.minY);
      const scale = clamp(Math.min((vw - pad * 2) / w, (vh - pad * 2) / h), minScale, maxScale);
      const x = (vw - w * scale) / 2 - b.minX * scale;
      const y = h * scale <= vh - pad * 2 ?
          (vh - h * scale) / 2 - b.minY * scale
        : pad - b.minY * scale;
      setView({ x, y, scale });
    };

    if (req.kind === "fit") {
      fit(layout.bounds, req.initial ? 0.3 : MIN_SCALE, req.initial ? 1 : MAX_SCALE);
    } else if (req.kind === "fitKeys") {
      fit(boundsOf(layout, req.keys), MIN_SCALE, 1);
    } else if (req.kind === "center") {
      const p = layout.pos.get(req.key);
      if (!p) return;
      setView((v) => {
        const cx = p.x;
        const cy = p.y + NODE_H / 2;
        if (req.ifOffscreen) {
          const sx = cx * v.scale + v.x;
          const sy = cy * v.scale + v.y;
          if (sx > 60 && sx < vw - 60 && sy > 60 && sy < vh - 60) return v;
        }
        const scale = clamp(v.scale, 0.8, 1.25);
        return { scale, x: vw / 2 - cx * scale, y: vh / 2 - cy * scale };
      });
    } else if (req.kind === "anchor") {
      // Keep the toggled node where it was on screen; if it opened, nudge the
      // new children into view without changing the zoom.
      const p = layout.pos.get(req.key);
      if (!p) {
        if (req.fallbackKey) requestView({ ...req, kind: "center", key: req.fallbackKey, ifOffscreen: true });
        return;
      }
      setView((v) => {
        const s = v.scale;
        let x = v.x + (req.x - p.x) * s;
        let y = v.y + (req.y - p.y) * s;
        const b = req.reveal ? boundsOf(layout, layout.kids.get(req.key) || []) : null;
        if (b) {
          const edge = 24;
          const bottom = b.maxY * s + y;
          const parentTop = p.y * s + y;
          if (bottom > vh - edge) y -= Math.min(bottom - (vh - edge), Math.max(0, parentTop - edge));
          const left = b.minX * s + x;
          const right = b.maxX * s + x;
          if (right - left <= vw - edge * 2) {
            if (left < edge) x += edge - left;
            else if (right > vw - edge) x -= right - (vw - edge);
          }
        }
        return { ...v, x, y };
      });
    }
  }, [layout, size, viewTick, usableW, viewportH, requestView]);

  // Short-lived visual cues
  useEffect(() => {
    if (!animateScope.all && animateScope.parent == null) return undefined;
    const t = setTimeout(() => setAnimateScope({ all: false, parent: null }), ENTER_ANIMATION_MS);
    return () => clearTimeout(t);
  }, [animateScope]);

  useEffect(() => {
    if (!flashId) return undefined;
    const t = setTimeout(() => setFlashId(null), FLASH_MS);
    return () => clearTimeout(t);
  }, [flashId]);

  // ── Mouse pan (coalesced to one update per frame) ──────────────────────────
  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    // Only pan if clicking on canvas background
    if (e.target !== wrapperRef.current && !e.target.classList.contains("joh-canvas")) return;
    panRef.current = { x: e.clientX, y: e.clientY, dx: 0, dy: 0, frame: 0 };
    setPanning(true);
    onSelectNode(null);
  }, [onSelectNode]);

  useEffect(() => {
    if (!panning) return undefined;
    const flush = () => {
      const p = panRef.current;
      if (!p) return;
      p.frame = 0;
      const { dx, dy } = p;
      if (!dx && !dy) return;
      p.dx = 0;
      p.dy = 0;
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    };
    const onMove = (e) => {
      const p = panRef.current;
      if (!p) return;
      p.dx += e.clientX - p.x;
      p.dy += e.clientY - p.y;
      p.x = e.clientX;
      p.y = e.clientY;
      if (!p.frame) p.frame = raf(flush);
    };
    const onUp = () => {
      const p = panRef.current;
      if (p && p.frame) cancelRaf(p.frame);
      flush();
      panRef.current = null;
      setPanning(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [panning]);

  const onWheel = useCallback((e) => {
    // If scrolling inside unassigned panel or modals, let standard scrolling happen naturally
    if (e.target.closest(".joh-unassigned-panel") || e.target.closest(".joh-modal") || e.target.closest(".joh-confirm-modal")) {
      return;
    }
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const rect = wrapperRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    setView((p) => {
      const newScale = clamp(p.scale * delta, MIN_SCALE, MAX_SCALE);
      const scaleRatio = newScale / p.scale;
      return {
        scale: newScale,
        x: mx - (mx - p.x) * scaleRatio,
        y: my - (my - p.y) * scaleRatio,
      };
    });
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  // ── Touch pan + pinch zoom (coalesced to one update per frame) ─────────────
  // One finger drags the chart (from anywhere but controls and the panel, so
  // a zoomed-in chart covered by cards can still be moved); two fingers zoom
  // about their midpoint. Each move is applied relative to the view at the
  // start of the gesture. Taps fall through as clicks, so selecting cards and
  // clearing the selection from the background work as with a mouse.
  const viewRef = useRef(view);
  useLayoutEffect(() => {
    viewRef.current = view;
  });
  const clearSelectionOnPan = useStableHandler(() => onSelectNode(null));
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    let gesture = null;
    let next = null;
    let frame = 0;
    const flush = () => {
      frame = 0;
      if (!next) return;
      const v = next;
      next = null;
      setView(v);
    };
    const local = (t, rect) => ({ x: t.clientX - rect.left, y: t.clientY - rect.top });
    const begin = (touches, target) => {
      const rect = el.getBoundingClientRect();
      // A gesture that changes finger count continues from where it got to.
      const start = next || viewRef.current;
      if (touches.length >= 2) {
        const a = local(touches[0], rect);
        const b = local(touches[1], rect);
        gesture = {
          kind: "pinch",
          rect,
          start,
          dist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
          mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        };
        setPanning(true);
      } else {
        gesture = {
          kind: "pan",
          rect,
          start,
          origin: local(touches[0], rect),
          moved: gesture ? gesture.moved : false,
          onBackground: target === el || target.classList.contains("joh-canvas"),
        };
      }
    };
    const onTouchStart = (e) => {
      if (e.target.closest(TOUCH_IGNORE_SELECTOR)) {
        gesture = null;
        return;
      }
      begin(e.touches, e.target);
    };
    const onTouchMove = (e) => {
      if (!gesture) return;
      const { rect, start } = gesture;
      if (gesture.kind === "pinch") {
        if (e.touches.length < 2) return;
        const a = local(e.touches[0], rect);
        const b = local(e.touches[1], rect);
        const dist = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
        const scale = clamp(start.scale * (dist / gesture.dist), MIN_SCALE, MAX_SCALE);
        // Keep the chart point that was under the fingers' midpoint under it.
        const wx = (gesture.mid.x - start.x) / start.scale;
        const wy = (gesture.mid.y - start.y) / start.scale;
        next = { scale, x: (a.x + b.x) / 2 - wx * scale, y: (a.y + b.y) / 2 - wy * scale };
      } else {
        const p = local(e.touches[0], rect);
        const dx = p.x - gesture.origin.x;
        const dy = p.y - gesture.origin.y;
        if (!gesture.moved) {
          if (Math.hypot(dx, dy) < TOUCH_SLOP_PX) return;
          gesture.moved = true;
          setPanning(true);
          if (gesture.onBackground) clearSelectionOnPan();
        }
        next = { ...start, x: start.x + dx, y: start.y + dy };
      }
      e.preventDefault();
      if (!frame) frame = raf(flush);
    };
    const onTouchEnd = (e) => {
      if (!gesture) return;
      if (e.touches.length > 0) {
        begin(e.touches, e.target);
        return;
      }
      if (frame) cancelRaf(frame);
      flush();
      gesture = null;
      setPanning(false);
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      if (frame) cancelRaf(frame);
    };
  }, [clearSelectionOnPan]);

  // ── Zoom control buttons (zoom about the centre of the visible area) ───────
  const zoomBy = (factor) => setView((v) => {
    const scale = clamp(+(v.scale * factor).toFixed(2), MIN_SCALE, MAX_SCALE);
    const cx = usableW / 2;
    const cy = viewportH / 2;
    const r = scale / v.scale;
    return { scale, x: cx - (cx - v.x) * r, y: cy - (cy - v.y) * r };
  });
  const zoomIn = () => zoomBy(1.2);
  const zoomOut = () => zoomBy(1 / 1.2);
  const fitAll = () => requestView({ kind: "fit" });
  const resetZoom = () => {
    const p = layout.pos.get(index.mainRootId);
    setView(p ? { scale: 1, x: usableW / 2 - p.x, y: 40 - p.y } : { x: 0, y: 60, scale: 1 });
  };

  // ── Panel visibility (keeps the chart centred in the uncovered area) ───────
  const showPanel = useStableHandler((open) => {
    if (open === unassignedOpen) return;
    setUnassignedOpen(open);
    if (unassignedCount > 0) {
      setView((v) => ({ ...v, x: v.x + (open ? -PANEL_W / 2 : PANEL_W / 2) }));
    }
  });

  // ── Disclosure handlers ────────────────────────────────────────────────────
  const toggleCollapse = useStableHandler((id) => {
    const p = layout.pos.get(id);
    const willExpand = !expanded.has(id);
    setDisclosure((d) => {
      const next = new Set(d.expanded);
      if (willExpand) next.add(id);
      else next.delete(id);
      return { ...d, expanded: next };
    });
    if (willExpand) setAnimateScope({ all: false, parent: id });
    if (p) requestView({ kind: "anchor", key: id, x: p.x, y: p.y, reveal: willExpand });
  });

  // Keeps the "more" card under the pointer so it can be clicked repeatedly;
  // once everything is shown, keeps the parent in place instead.
  const showMore = useStableHandler((parentId, all = false) => {
    const stub = moreKey(parentId);
    const anchorKey = all ? parentId : stub;
    const p = layout.pos.get(anchorKey);
    setChildLimit((m) => {
      const next = new Map(m);
      next.set(parentId, all ? Number.MAX_SAFE_INTEGER : childLimitFor(m, parentId) + CHILD_PAGE_SIZE);
      return next;
    });
    setAnimateScope({ all: false, parent: parentId });
    if (p) requestView({ kind: "anchor", key: anchorKey, fallbackKey: parentId, x: p.x, y: p.y, reveal: false });
  });
  const showAll = useStableHandler((parentId) => showMore(parentId, true));

  const showOverview = useStableHandler(() => {
    setDisclosure((d) => ({ ...d, expanded: computeInitialExpanded(index) }));
    setChildLimit(new Map());
    setPinned(new Set());
    setAnimateScope({ all: true, parent: null });
    requestView({ kind: "fit" });
  });

  const expandAll = useStableHandler(() => {
    setDisclosure((d) => ({ ...d, expanded: allExpandable(index) }));
    requestView({ kind: "fit" });
  });

  // Expand every ancestor of `ids` and pin any that sit beyond a parent's page.
  const openPathsTo = (ids) => {
    const add = new Set();
    const pins = [];
    ids.forEach((id) => {
      const ancestors = getAncestors(index, id);
      ancestors.forEach((a) => add.add(a));
      [...ancestors.slice(1), id].forEach((c) => {
        const parent = index.byId.get(c)?.parentId;
        if ((index.childIndex.get(c) ?? 0) >= childLimitFor(childLimit, parent)) pins.push(c);
      });
    });
    setDisclosure((d) => {
      let next = null;
      add.forEach((a) => {
        if (d.expanded.has(a)) return;
        next = next || new Set(d.expanded);
        next.add(a);
      });
      return next ? { ...d, expanded: next } : d;
    });
    if (pins.length) {
      setPinned((prev) => {
        const next = new Set(prev);
        pins.forEach((c) => next.add(c));
        return next.size === prev.size ? prev : next;
      });
    }
  };

  // ── Search navigation ──────────────────────────────────────────────────────
  const revealJob = useStableHandler((id, opts = {}) => {
    const node = index.byId.get(id);
    if (!node) return;
    if (!index.inTree.has(id)) {
      if (!node.parentId && id !== index.mainRootId) {
        showPanel(true);
        setPanelFocus({ id, token: Date.now() });
      } else {
        toast.info(`"${node.name}" reports to a position that is not in this hierarchy.`);
      }
      return;
    }
    openPathsTo([id]);
    onSelectNode(id);
    setJobFocusId(id);
    setFlashId(id);
    requestView({ kind: "center", key: id, ifOffscreen: Boolean(opts.ifOffscreen) });
  });

  const focusFunction = useStableHandler((key) => {
    const fn = searchIndex.functionByKey.get(key);
    setFunctionFocusKey(key);
    if (!fn) return;
    const heads = functionHeads(index, fn.jobIds);
    if (heads.length === 0) {
      if (fn.jobIds.length > 0) {
        showPanel(true);
        toast.info(`No "${fn.name}" jobs are placed in the hierarchy yet. They are listed under Unassigned Positions.`);
      }
      return;
    }
    openPathsTo(heads);
    requestView({ kind: "fitKeys", keys: heads });
  });

  const clearFunctionFocus = useCallback(() => setFunctionFocusKey(null), []);

  // After a reload, bring the job the user just created or placed into view.
  useEffect(() => {
    const id = pendingReveal.current;
    if (!id || !index.byId.has(id)) return;
    pendingReveal.current = null;
    revealJob(id, { ifOffscreen: true });
  }, [index, revealJob]);

  // ── Open form modals ───────────────────────────────────────────────────────
  const openCreate = useCallback((parentId = null, initialData = null) => {
    setFormModal({ open: true, mode: "create", initialData, parentId, saving: false, error: "" });
  }, []);

  const openEdit = useCallback(async (node) => {
    // Open modal immediately with basic node data, then enrich with full job relations
    const baseData = {
      ...node,
      parentId: node.parentId,
      skills:   [],
      raciRows: [],
    };
    setFormModal({
      open: true,
      mode: "edit",
      initialData: baseData,
      parentId: node.parentId,
      nodeId: node.id,
      saving: false,
      error: "",
    });
    // Fetch full relations in the background to populate skills + raci
    try {
      const full = await getJobWithRelations(node.id);
      if (!full) return;
      const extractedSkills = Array.isArray(full.jobSkills)
        ? full.jobSkills.map((js) => ({ name: js?.skill?.name || "", level: js?.skill_level?.level_name || "" }))
        : [];
      const taskJobs = full?.taskJobs || full?.task_jobs || full?.jobTasks || full?.job_tasks || [];
      const extractedRaci = Array.isArray(taskJobs)
        ? taskJobs
            .map((tj) => ({
              task_id: tj?.task_id ?? tj?.task?.task_id ?? tj?.task?.id,
              role: tj?.role ?? null,
              time_allocation_percentage: tj?.time_allocation_percentage != null ? Number(tj.time_allocation_percentage) : null,
            }))
            .filter((r) => r.task_id != null)
        : [];
      setFormModal((prev) => ({
        ...prev,
        initialData: {
          ...prev.initialData,
          description:    full.description    || prev.initialData.description,
          hourlyRate:     full.hourlyRate     ?? prev.initialData.hourlyRate,
          currencyType:   full.currencyType   || prev.initialData.currencyType || "USD",
          maxHoursPerDay: full.maxHoursPerDay ?? prev.initialData.maxHoursPerDay,
          staff_count:    full.staff_count    ?? prev.initialData.staff_count,
          daysPerWeek:    full.daysPerWeek    ?? full.days_per_week ?? prev.initialData.daysPerWeek,
          capacityBuffer: full.capacityBuffer ?? full.capacity_buffer ?? prev.initialData.capacityBuffer,
          skills:   extractedSkills,
          raciRows: extractedRaci,
        },
      }));
    } catch {
      // non-fatal: modal already open with basic data
    }
  }, []);

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSave = useCallback(async (form) => {
    const name    = String(form.name    || "").trim();
    const jobCode = String(form.jobCode || "").trim();
    if (!name)    { setFormModal((p) => ({ ...p, error: "Job Name is required" }));    return; }
    if (!jobCode) { setFormModal((p) => ({ ...p, error: "Job Code is required" }));    return; }

    setFormModal((p) => ({ ...p, saving: true, error: "" }));
    try {
      const payload = {
        name,
        jobCode,
        description:    form.description    || "",
        employeeName:   form.employeeName   || "",
        function_id:    form.function_id    ? Number(form.function_id)    : null,
        company_id:     companyId           ? Number(companyId)           : null,
        category_id:    form.category_id   ? Number(form.category_id)   : null,
        status_id:      form.status_id     ? Number(form.status_id)      : null,
        jobLevel:       form.jobLevel       || "",
        hourlyRate:     form.hourlyRate     !== "" ? Number(form.hourlyRate)     : null,
        currencyType:   form.currencyType   || "USD",
        maxHoursPerDay: form.maxHoursPerDay !== "" ? Number(form.maxHoursPerDay) : null,
        staff_count:    form.staff_count    !== "" ? Number(form.staff_count)    : undefined,
        daysPerWeek:    form.daysPerWeek    !== "" ? Number(form.daysPerWeek)    : null,
        capacityBuffer: form.capacityBuffer !== "" ? Number(form.capacityBuffer) : null,
        skills: (form.skills || [])
          .filter((s) => String(s.name).trim() && String(s.level))
          .map((s) => ({ name: s.name.trim(), level: s.level })),
        raci: form.raciRows || [],
      };

      if (formModal.mode === "edit") {
        // parent_job_id: "" means no parent → null
        payload.parent_job_id = form.parent_job_id !== "" ? Number(form.parent_job_id) : null;
        await updateJob(formModal.nodeId, payload);
        toast.success(`"${name}" updated successfully`);
      } else {
        const res = await createJob(payload);
        // If a parent is specified, reparent the new job
        const newJobId = res?.job_id ?? res?.id;
        if (newJobId && formModal.parentId && companyId) {
          try {
            await reparentJob(companyId, newJobId, formModal.parentId);
          } catch {
            // non-fatal: node created but not linked; reload will show correct state
          }
        }
        if (newJobId) pendingReveal.current = String(newJobId);
        toast.success(`"${name}" created successfully`);
      }
      setFormModal((p) => ({ ...p, open: false }));
      if (typeof onReload === "function") onReload();
    } catch (e) {
      setFormModal((p) => ({ ...p, saving: false, error: e?.message || "Failed to save. Please try again." }));
    }
  }, [formModal.mode, formModal.nodeId, formModal.parentId, companyId, onReload]);

  // ── Assign-parent handler (from AssignParentModal or D&D drop) ────────────
  const handleAssignParent = useCallback(async (childId, newParentId) => {
    if (!childId || !newParentId || !companyId) return;
    setAssignModal((p) => ({ ...p, saving: true, error: "" }));
    try {
      await reparentJob(companyId, childId, Number(newParentId));
      toast.success("Position assigned to hierarchy successfully");
      setAssignModal((p) => ({ ...p, open: false }));
      pendingReveal.current = String(childId);
      if (typeof onReload === "function") onReload();
    } catch (e) {
      setAssignModal((p) => ({ ...p, saving: false, error: e?.message || "Failed to assign parent" }));
    }
  }, [companyId, onReload]);

  const handleAssignExistingJob = useCallback(async (childId, newParentId) => {
    if (!childId || !companyId) return;
    setExistingJobModal((p) => ({ ...p, saving: true, error: "" }));
    try {
      await reparentJob(companyId, childId, newParentId ? Number(newParentId) : null);
      toast.success("Position assigned to hierarchy successfully");
      setExistingJobModal({ open: false, parentId: null, saving: false, error: "" });
      pendingReveal.current = String(childId);
      if (typeof onReload === "function") onReload();
    } catch (e) {
      setExistingJobModal((p) => ({ ...p, saving: false, error: e?.message || "Failed to assign job" }));
    }
  }, [companyId, onReload]);

  // ── Unassign parent handler (move to unassigned positions) ────────────────
  const handleUnassignNode = useStableHandler((id) => {
    const node = index.byId.get(id);
    if (!node) return;
    setUnassignModal({
      open: true,
      nodeId: id,
      jobName: node.name,
      processing: false,
    });
  });

  const confirmUnassign = useCallback(async () => {
    const { nodeId, jobName } = unassignModal;
    if (!nodeId || !companyId) return;
    setUnassignModal((p) => ({ ...p, processing: true }));

    // Collect all descendants
    const descendants = getDescendants(index, nodeId);

    try {
      if (descendants.length > 0) {
        // Bulk unassign all (root + all descendants) by clearing parent relationships
        const changes = [nodeId, ...descendants].map((id) => ({
          childJobId: id,
          newParentJobId: null,
        }));
        await bulkReparentJobs(companyId, changes);
      } else {
        // Single unassign
        await reparentJob(companyId, nodeId, null);
      }

      toast.success(descendants.length > 0 ? "Positions removed from hierarchy successfully" : `"${jobName}" moved to unassigned positions`);
      setUnassignModal({ open: false, nodeId: null, jobName: "", processing: false });
      if (typeof onClearDesignatedRoot === "function") {
        onClearDesignatedRoot();
      }
      if (typeof onReload === "function") onReload();
    } catch (e) {
      toast.error(e?.message || "Failed to remove parent");
      setUnassignModal((p) => ({ ...p, processing: false }));
    }
  }, [unassignModal, companyId, index, onReload, onClearDesignatedRoot]);

  // ── Drag handlers for unassigned panel cards ──────────────────────────────
  const endDrag = useCallback(() => {
    dragRef.current = null;
    setDragNodeId(null);
    setDropTargetId(null);
    if (dragGhostRef.current) {
      dragGhostRef.current.remove();
      dragGhostRef.current = null;
    }
  }, []);

  const handleUnassignedDragStart = useCallback((e, node) => {
    dragRef.current = node.id;
    setDragNodeId(node.id);
    // Create a custom ghost element
    const ghost = document.createElement("div");
    ghost.style.cssText = `
      position: fixed; top: -9999px; left: -9999px; z-index: 9999;
      background: #fff; border: 2px solid #3b82f6; border-radius: 10px;
      padding: 10px 14px; font-family: inherit; font-size: 13px;
      font-weight: 700; color: #1e293b; box-shadow: 0 8px 24px rgba(59,130,246,0.25);
      white-space: nowrap; display: flex; align-items: center; gap: 8px;
    `;
    const icon = document.createElement("span");
    icon.style.fontSize = "16px";
    icon.textContent = "🔗";
    const label = document.createElement("span");
    label.textContent = node.name;
    ghost.append(icon, label);
    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", node.id);
  }, []);

  // A dragged card can scroll out of the windowed list; end the drag anyway.
  useEffect(() => {
    if (!dragNodeId) return undefined;
    const end = () => setTimeout(endDrag, 0);
    window.addEventListener("dragend", end);
    window.addEventListener("drop", end);
    return () => {
      window.removeEventListener("dragend", end);
      window.removeEventListener("drop", end);
    };
  }, [dragNodeId, endDrag]);

  const handleNodeDragOver = useCallback((e, nodeId) => {
    // Only accept drags from unassigned cards
    const dragging = dragRef.current;
    if (!dragging || nodeId === dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetId(nodeId);
  }, []);

  const handleNodeDragLeave = useCallback((e) => {
    // Only clear if we left the element itself, not a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetId(null);
    }
  }, []);

  const handleNodeDrop = useStableHandler(async (e, parentNodeId) => {
    e.preventDefault();
    const childId = dragRef.current || e.dataTransfer.getData("text/plain");
    endDrag();
    if (!childId || !parentNodeId || childId === parentNodeId) return;
    // Directly reparent without the modal
    try {
      await reparentJob(companyId, childId, Number(parentNodeId));
      toast.success("Position assigned to hierarchy successfully");
      pendingReveal.current = String(childId);
      if (typeof onReload === "function") onReload();
    } catch (err) {
      toast.error(err?.message || "Failed to assign parent");
    }
  });

  const openAssignModal = useCallback((node) => {
    setAssignModal({ open: true, node, saving: false, error: "" });
  }, []);
  const closePanel = useCallback(() => showPanel(false), [showPanel]);

  const panelJobs = useMemo(
    () => index.unassigned.map((n) => ({ id: n.id, name: n.name, code: n.jobCode, functionName: n.functionName })),
    [index],
  );

  // Stable handlers shared by every card
  const addChild = useCallback((id) => setChoiceModal({ open: true, parentId: id }), []);
  const addSibling = useStableHandler((id) => {
    setChoiceModal({ open: true, parentId: index.byId.get(id)?.parentId ?? null });
  });
  const handlers = useMemo(() => ({
    onSelect: selectNode,
    onEdit: openEdit,
    onAddChild: addChild,
    onAddSibling: addSibling,
    onUnassign: handleUnassignNode,
    onToggle: toggleCollapse,
    onShowMore: showMore,
    onShowAll: showAll,
    onNodeDragOver: handleNodeDragOver,
    onNodeDragLeave: handleNodeDragLeave,
    onNodeDrop: handleNodeDrop,
  }), [selectNode, openEdit, addChild, addSibling, handleUnassignNode, toggleCollapse, showMore, showAll, handleNodeDragOver, handleNodeDragLeave, handleNodeDrop]);

  const zoomPct = Math.round(view.scale * 100);
  const assignedCount = index.total - unassignedCount;

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column" }}>
      {/* Chart toolbar: search and zoom */}
      <div className="joh-chart-toolbar">
        <OrgSearch
          index={index}
          searchIndex={searchIndex}
          avatarGradient={avatarGradient}
          focusedFunctionKey={focus ? focus.key : null}
          focusedJob={jobFocus}
          onPickJob={revealJob}
          onPickFunction={focusFunction}
          onClearFunction={clearFunctionFocus}
          onClearJob={clearJobFocus}
        />
        <div className="joh-zoom-group">
          <button id="joh-zoom-out" className="joh-zoom-btn" onClick={zoomOut} title="Zoom out">−</button>
          <span className="joh-zoom-label">{zoomPct}%</span>
          <button id="joh-zoom-in" className="joh-zoom-btn" onClick={zoomIn} title="Zoom in">+</button>
          <button id="joh-zoom-fit" className="joh-zoom-btn" onClick={fitAll} title="Fit all" style={{ fontSize: "0.75rem" }}>⤢</button>
          <button id="joh-zoom-reset" className="joh-zoom-btn" onClick={resetZoom} title="Reset zoom" style={{ fontSize: "0.7rem" }}>1:1</button>
          <span className="joh-zoom-sep" aria-hidden="true" />
          <button id="joh-expand-all" className="joh-zoom-btn" onClick={expandAll} title="Expand every level">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" />
            </svg>
          </button>
          <button id="joh-collapse-all" className="joh-zoom-btn" onClick={showOverview} title="Collapse to the overview">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m7 20 5-5 5 5" /><path d="m7 4 5 5 5-5" />
            </svg>
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={wrapperRef}
        className={`joh-canvas-wrapper joh-touch-canvas${panning ? " panning" : ""}`}
        onMouseDown={onMouseDown}
        onClick={(e) => { if (e.target === wrapperRef.current) onSelectNode(null); }}
      >
        <div
          className={`joh-canvas${panning ? " panning" : ""}`}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            width: canvasW,
            height: canvasH,
            transition: panning ? "none" : "transform 0.12s ease-out",
          }}
        >
          <EdgeLayer rect={cullRect} d={edgePath} highlight={highlightPath} teams={teamRects} />
          <NodeLayer
            items={culledItems}
            index={index}
            expanded={expanded}
            selectedId={selectedId}
            highlightId={highlightId}
            pathIds={pathIds}
            dropTargetId={dragNodeId ? dropTargetId : null}
            focus={focus}
            flashId={flashId}
            animateAll={animateScope.all}
            animateParent={animateScope.parent}
            compact={compact}
            handlers={handlers}
          />
        </div>

        {/* Floating restore tab for unassigned positions panel */}
        {!unassignedOpen && unassignedCount > 0 && (
          <button
            className="joh-unassigned-panel-trigger"
            onClick={() => showPanel(true)}
            title="Show unassigned positions panel"
          >
            📂 Unassigned Positions ({formatCount(unassignedCount)})
          </button>
        )}

        {/* Unassigned positions floating panel */}
        {unassignedCount > 0 && (
          <UnassignedPanel
            open={unassignedOpen}
            jobs={panelJobs}
            hint="🖱 Drag card onto a position node, or double-click to assign"
            cardTitle="Drag onto a parent node, or double-click to assign via form"
            assignTitle="Assign a parent position"
            draggingId={dragNodeId}
            focusRequest={panelFocus}
            avatarGradient={avatarGradient}
            initials={initials}
            onClose={closePanel}
            onDragStart={handleUnassignedDragStart}
            onDragEnd={endDrag}
            onCardDoubleClick={openAssignModal}
            onAssign={openAssignModal}
          />
        )}

        {/* Minimap (placed in the bottom left corner) */}
        <Minimap
          data={minimap}
          selectedPos={selectedId ? layout.pos.get(selectedId) : null}
          view={view}
          viewportW={viewportW}
          viewportH={viewportH}
        />

        {/* Status bar */}
        <div className="joh-status-bar">
          {formatCount(assignedCount)} of {formatCount(index.total)} positions assigned
          {unassignedCount > 0 ? ` • ${formatCount(unassignedCount)} unassigned` : ""}
          {` • ${formatCount(shownCount)} shown`}
          {focus ? ` • highlighting ${focus.name}` : ""}
        </div>
      </div>

      {/* Modals */}
      <JobFormModal
        open={formModal.open}
        mode={formModal.mode}
        initialData={formModal.initialData}
        functions={functions}
        categories={categories}
        statuses={statuses}
        allTasks={allTasks}
        jobOptions={jobOptions}
        nodeId={formModal.nodeId}
        saving={formModal.saving}
        error={formModal.error}
        onSave={handleSave}
        onClose={() => setFormModal((p) => ({ ...p, open: false }))}
      />

      <AssignParentModal
        open={assignModal.open}
        node={assignModal.node}
        jobOptions={jobOptions}
        nodeId={assignModal.node?.id}
        saving={assignModal.saving}
        error={assignModal.error}
        onSave={(newParentId) => handleAssignParent(assignModal.node?.id, newParentId)}
        onClose={() => setAssignModal((p) => ({ ...p, open: false, error: "" }))}
      />

      <UnassignConfirmModal
        open={unassignModal.open}
        jobName={unassignModal.jobName}
        hasChildren={(index.childrenOf.get(unassignModal.nodeId) || []).length > 0}
        processing={unassignModal.processing}
        onConfirm={confirmUnassign}
        onCancel={() => setUnassignModal({ open: false, nodeId: null, jobName: "", processing: false })}
      />

      <ChoiceModal
        open={choiceModal.open}
        onNewJob={() => {
          setChoiceModal({ open: false, parentId: null });
          openCreate(choiceModal.parentId);
        }}
        onExistingJob={() => {
          setChoiceModal({ open: false, parentId: null });
          setExistingJobModal({ open: true, parentId: choiceModal.parentId, saving: false, error: "" });
        }}
        onClose={() => setChoiceModal({ open: false, parentId: null })}
      />

      <ExistingJobModal
        open={existingJobModal.open}
        parentId={existingJobModal.parentId}
        unassignedJobs={index.unassigned}
        saving={existingJobModal.saving}
        error={existingJobModal.error}
        onAssign={handleAssignExistingJob}
        onClose={() => setExistingJobModal({ open: false, parentId: null, saving: false, error: "" })}
      />
    </div>
  );
}

// ─── Root placeholder (Screen 1) ──────────────────────────────────────────────
function RootPlaceholder({ onClick, onDropJob }) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      className="joh-canvas-wrapper"
      style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const jobId = e.dataTransfer.getData("text/plain");
        if (jobId && typeof onDropJob === "function") {
          onDropJob(jobId);
        }
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, maxWidth: "100%", padding: "0 16px" }}>
        {/* Single placeholder node */}
        <div
          className="joh-root-placeholder-card"
          style={{
            border: isDragOver ? '2px solid #2563eb' : '2px dashed #93c5fd',
            backgroundColor: isDragOver ? '#dbeafe' : '#eff6ff',
            borderRadius: '12px',
            padding: '30px clamp(16px, 6vw, 40px)',
            textAlign: 'center',
            cursor: 'pointer',
            minWidth: 'min(320px, 100%)',
            transition: 'all 0.2s',
            boxShadow: isDragOver ? '0 0 0 4px rgba(37, 99, 235, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            transform: isDragOver ? 'scale(1.04)' : 'scale(1)'
          }}
          onClick={onClick}
          onDoubleClick={onClick}
          title="Click to add position or drag an unassigned job here"
        >
          <div style={{
            backgroundColor: isDragOver ? '#bfdbfe' : '#dbeafe',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            fontSize: '24px',
            transition: 'background-color 0.2s'
          }}>
            🏢
          </div>
          <div style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '18px', marginBottom: '8px' }}>
            {isDragOver ? "Drop to Set as Root Position" : "Add Root Job"}
          </div>
          <div style={{ color: '#64748b', fontSize: '14px' }}>
            {isDragOver ? "Release mouse to place position as head of hierarchy" : "Click to select/create, or drag an unassigned job here"}
          </div>
        </div>
        <div style={{ fontSize: "0.85rem", color: "#64748b", textAlign: "center", maxWidth: 320, lineHeight: 1.5 }}>
          💡 Drag an unassigned job card here to set it as root, then drag other jobs onto it to form children.
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const CLevelOrgChartPage = () => {
  const [companies, setCompanies]         = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companyQuery, setCompanyQuery]   = useState("");
  const [nodes, setNodes]                 = useState([]);
  const [functions, setFunctions]         = useState([]);
  const [categories, setCategories]       = useState([]);
  const [statuses, setStatuses]           = useState([]);
  const [allTasks, setAllTasks]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Guards against responses for a company (or load) that is no longer current
  const loadSeq = useRef(0);
  const currentCompany = useRef("");

  // Modals for root creation (Screen 1)
  const [rootFormOpen, setRootFormOpen]   = useState(false);
  const [rootFormSaving, setRootFormSaving] = useState(false);
  const [rootFormError, setRootFormError] = useState("");

  const [rootChoiceOpen, setRootChoiceOpen] = useState(false);
  const [rootExistingOpen, setRootExistingOpen] = useState(false);
  const [rootExistingSaving, setRootExistingSaving] = useState(false);
  const [rootExistingError, setRootExistingError] = useState("");
  const [rootUnassignedJobs, setRootUnassignedJobs] = useState([]);
  const [designatedRootId, setDesignatedRootId] = useState(null);

  // Panel state for Screen 1 (closed on phones, where it would cover the canvas)
  const [panelOpen, setPanelOpen] = useState(() => !isPhoneViewport());

  const hasHierarchy = nodes.length > 0 && (nodes.some((n) => n.parentId !== null) || designatedRootId !== null);
  const showScreen1  = Boolean(selectedCompanyId) && !loading && !hasHierarchy;
  // Keep the chart mounted while it reloads so zoom and open branches survive edits
  const showScreen2  = Boolean(selectedCompanyId) && hasHierarchy;

  // Reset designated root when company changes
  useEffect(() => {
    setDesignatedRootId(null);
  }, [selectedCompanyId]);

  // Unassigned jobs for Screen 1 — only needed while there is no hierarchy
  useEffect(() => {
    if (!selectedCompanyId) {
      setRootUnassignedJobs([]);
      return undefined;
    }
    if (!showScreen1) return undefined;
    let cancelled = false;
    getJobsByCompanyId(selectedCompanyId).then((jobs) => {
      if (cancelled) return;
      const arr = Array.isArray(jobs) ? jobs : [];
      setRootUnassignedJobs(arr.filter(j => !j.parent_job_id));
    }).catch(() => { if (!cancelled) setRootUnassignedJobs([]); });
    return () => { cancelled = true; };
  }, [selectedCompanyId, nodes, showScreen1]);

  const rootPanelJobs = useMemo(
    () => rootUnassignedJobs.map((j) => ({
      id: String(j.id || j.job_id),
      name: j.name,
      code: j.jobCode || j.job_code || "",
      functionName: j.function_name || j.Function?.name || "",
    })),
    [rootUnassignedJobs],
  );

  // Load companies once
  useEffect(() => {
    (async () => {
      try {
        const data = await getCompanyNames();
        setCompanies(Array.isArray(data) ? data : []);
      } catch {
        toast.error("Failed to load companies");
      } finally {
        setCompaniesLoading(false);
      }
    })();
  }, []);

  // Load categories + statuses once
  useEffect(() => {
    (async () => {
      const [cats, stats] = await Promise.all([
        getJobCategories().catch(() => []),
        getJobStatuses().catch(() => []),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setStatuses(Array.isArray(stats) ? stats : []);
    })();
  }, []);

  const companyOptions = useMemo(
    () => companies.map((c) => ({
      value: String(c.company_id),
      label: `${c.name}${c.companyCode ? ` (${c.companyCode})` : ""}`,
    })),
    [companies],
  );
  const selectedCompanyLabel = useMemo(
    () => companyOptions.find((o) => o.value === String(selectedCompanyId))?.label || "",
    [companyOptions, selectedCompanyId],
  );
  useEffect(() => {
    setCompanyQuery(selectedCompanyLabel);
  }, [selectedCompanyLabel]);

  // Normalize a flat job list into tree nodes
  const normalizeNodes = useCallback((jobs = [], funcsMap = {}) => {
    return jobs.map((j) => ({
      id:             String(j.job_id ?? j.id),
      parentId:       j.parent_job_id ? String(j.parent_job_id) : null,
      name:           j.name           || "",
      jobCode:        j.jobCode        || j.job_code        || "",
      description:    j.description    || "",
      employeeName:   j.employeeName   || j.employee_name   || "",
      function_id:    j.function_id    ?? j.Function?.function_id ?? null,
      functionName:   j.Function?.name || funcsMap[j.function_id] || "",
      category_id:    j.job_category_id ?? j.category_id    ?? null,
      status_id:      j.job_status_id  ?? j.status_id       ?? null,
      jobLevel:       j.jobLevel       || j.job_level?.level_name || "",
      hourlyRate:     j.hourlyRate     ?? j.hourly_rate     ?? null,
      currencyType:   j.currencyType   || j.currency_type   || "USD",
      maxHoursPerDay: j.maxHoursPerDay ?? j.max_hours_per_day ?? null,
      staff_count:    j.staff_count    ?? null,
      daysPerWeek:    j.daysPerWeek    ?? j.days_per_week   ?? null,
      capacityBuffer: j.capacityBuffer ?? j.capacity_buffer ?? null,
    }));
  }, []);

  // Load hierarchy for a company
  const loadHierarchy = useCallback(async (companyId) => {
    const seq = ++loadSeq.current;
    if (!companyId) { setNodes([]); setFunctions([]); return; }
    setLoading(true);
    setSelectedNodeId(null);
    try {
      const [jobs, funcs] = await Promise.all([
        getJobHierarchyFlat(companyId).catch(() => []),
        getFunctionsByCompany(companyId).catch(() => []),
      ]);
      if (seq !== loadSeq.current) return;
      const funcsArray = Array.isArray(funcs) ? funcs : [];
      setFunctions(funcsArray);
      const funcsMap = {};
      funcsArray.forEach((f) => { funcsMap[f.function_id] = f.name; });
      const all = Array.isArray(jobs) ? jobs : [];
      // Show all jobs (connected or not) to support full hierarchy view
      setNodes(normalizeNodes(all, funcsMap));
    } catch (e) {
      if (seq !== loadSeq.current) return;
      toast.error(e?.message || "Failed to load hierarchy");
      setNodes([]);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [normalizeNodes]);

  // Company selection
  const handleCompanyChange = useCallback((val) => {
    currentCompany.current = val;
    setSelectedCompanyId(val);
    setNodes([]);
    setSelectedNodeId(null);
    setAllTasks([]);
    if (val) {
      loadHierarchy(val);
      getTasksByCompanyId(val).then((tasks) => {
        if (currentCompany.current === val) setAllTasks(Array.isArray(tasks) ? tasks : []);
      }).catch(() => { if (currentCompany.current === val) setAllTasks([]); });
    } else {
      loadSeq.current += 1;
      setLoading(false);
    }
  }, [loadHierarchy]);

  // Reload (after save/delete)
  const handleReload = useCallback(() => {
    if (selectedCompanyId) loadHierarchy(selectedCompanyId);
  }, [selectedCompanyId, loadHierarchy]);

  const clearDesignatedRoot = useCallback(() => setDesignatedRootId(null), []);

  // Root job creation (Screen 1)
  const handleRootSave = useCallback(async (form) => {
    const name    = String(form.name    || "").trim();
    const jobCode = String(form.jobCode || "").trim();
    if (!name)    { setRootFormError("Job Name is required");    return; }
    if (!jobCode) { setRootFormError("Job Code is required"); return; }
    setRootFormSaving(true);
    setRootFormError("");
    try {
      await createJob({
        name,
        jobCode,
        description:    form.description    || "",
        employeeName:   form.employeeName   || "",
        function_id:    form.function_id    ? Number(form.function_id)    : null,
        company_id:     Number(selectedCompanyId),
        category_id:    form.category_id   ? Number(form.category_id)   : null,
        status_id:      form.status_id     ? Number(form.status_id)      : null,
        jobLevel:       form.jobLevel       || "",
        hourlyRate:     form.hourlyRate     !== "" ? Number(form.hourlyRate)     : null,
        currencyType:   form.currencyType   || "USD",
        maxHoursPerDay: form.maxHoursPerDay !== "" ? Number(form.maxHoursPerDay) : null,
        staff_count:    form.staff_count    !== "" ? Number(form.staff_count)    : undefined,
        daysPerWeek:    form.daysPerWeek    !== "" ? Number(form.daysPerWeek)    : null,
        capacityBuffer: form.capacityBuffer !== "" ? Number(form.capacityBuffer) : null,
        skills: [],
      });
      toast.success(`"${name}" created as root position`);
      setRootFormOpen(false);
      handleReload();
    } catch (e) {
      setRootFormError(e?.message || "Failed to create job");
    } finally {
      setRootFormSaving(false);
    }
  }, [selectedCompanyId, handleReload]);

  const handleOpenRootExisting = useCallback(async () => {
    setRootChoiceOpen(false);
    setRootExistingOpen(true);
    try {
      const jobs = await getJobsByCompanyId(selectedCompanyId);
      const arr = Array.isArray(jobs) ? jobs : [];
      setRootUnassignedJobs(arr.filter(j => !j.parent_job_id));
    } catch {
      setRootUnassignedJobs([]);
    }
  }, [selectedCompanyId]);

  const handleRootAssignExisting = useCallback(async (childId, parentId) => {
    if (!childId || !selectedCompanyId) return;
    setRootExistingSaving(true);
    setRootExistingError("");
    try {
      await reparentJob(selectedCompanyId, childId, parentId ? Number(parentId) : null);
      toast.success("Position assigned successfully");
      setRootExistingOpen(false);
      handleReload();
    } catch (e) {
      setRootExistingError(e?.message || "Failed to assign job");
    } finally {
      setRootExistingSaving(false);
    }
  }, [selectedCompanyId, handleReload]);

  const handleDropRoot = useCallback((jobId) => {
    if (!jobId) return;
    const idStr = String(jobId);
    const found = rootUnassignedJobs.find(j => String(j.id || j.job_id) === idStr)
               || nodes.find(n => String(n.id) === idStr);
    const jobName = found ? found.name : "Position";
    setDesignatedRootId(idStr);
    toast.success(`"${jobName}" placed as root position. Drag another job onto it to add children.`);
  }, [rootUnassignedJobs, nodes]);

  // Screen 1 panel handlers
  const handleRootCardDragStart = useCallback((e, job) => {
    e.dataTransfer.setData("text/plain", job.id);
    e.dataTransfer.effectAllowed = "move";
  }, []);
  const openRootChoice = useCallback(() => setRootChoiceOpen(true), []);
  const closeRootPanel = useCallback(() => setPanelOpen(false), []);

  return (
    <div className="joh-page">
      {/* ── Toolbar ── */}
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Organization Chart - Structured Hierarchy</h2>
            <p className="page-subtitle">
              View and build the organizational hierarchy
            </p>
          </div>
          <div className="roles-toolbar">
            <div
              className="roles-search joh-company-picker"
              onBlur={() => setCompanyQuery(selectedCompanyLabel)}
            >
              <SearchableDropdown
                id="joh-company-selector"
                className="search-input searchable-combobox"
                value={companyQuery}
                options={companyOptions}
                placeholder={companiesLoading ? "Loading companies…" : "Search and select a company…"}
                clearOnFocus
                disabled={companiesLoading}
                onChange={(text, option) => {
                  if (!option) {
                    setCompanyQuery(text);
                    return;
                  }
                  setCompanyQuery(option.label);
                  if (option.value !== String(selectedCompanyId)) handleCompanyChange(option.value);
                }}
              />
            </div>

            {selectedCompanyId && (
              <button
                id="joh-refresh-btn"
                className="secondary-btn"
                onClick={handleReload}
                disabled={loading}
                title="Reload hierarchy"
              >
                Refresh
              </button>
            )}

            {hasHierarchy && (
              <span className="joh-count-pill">
                {formatCount(nodes.length)} position{nodes.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="joh-page-body" style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {/* Loading overlay */}
        {loading && (
          <div className="joh-loading-overlay">
            <div className="joh-loading-spinner">
              <div className="joh-spinner-ring" />
              <div className="joh-loading-text">Loading hierarchy…</div>
            </div>
          </div>
        )}

        {/* No company selected */}
        {!selectedCompanyId && !loading && (
          <div className="joh-empty-state" style={{ height: "100%", position: "absolute", inset: 0 }}>
            <div className="joh-empty-icon">🏢</div>
            <div className="joh-empty-title">Select a Company</div>
            <div className="joh-empty-subtitle">
              Choose a company from the dropdown above to view or build its organizational hierarchy.
            </div>
          </div>
        )}

        {/* Screen 1 — no hierarchy yet */}
        {showScreen1 && (
          <>
            <RootPlaceholder
              onClick={() => setRootChoiceOpen(true)}
              onDropJob={handleDropRoot}
            />

            {/* Unassigned side panel on Screen 1 */}
            {!panelOpen && rootUnassignedJobs.length > 0 && (
              <button
                className="joh-unassigned-panel-trigger"
                onClick={() => setPanelOpen(true)}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 16,
                  zIndex: 10,
                }}
              >
                📂 Unassigned Positions ({formatCount(rootUnassignedJobs.length)})
              </button>
            )}

            {rootUnassignedJobs.length > 0 && (
              <UnassignedPanel
                open={panelOpen}
                jobs={rootPanelJobs}
                hint="🖱 Drag card onto canvas to set as root, or click 🔗 to assign"
                cardTitle="Drag onto canvas to set as root position, or click to assign"
                assignTitle="Assign position"
                avatarGradient={avatarGradient}
                initials={initials}
                onClose={closeRootPanel}
                onDragStart={handleRootCardDragStart}
                onCardClick={openRootChoice}
                onCardDoubleClick={handleOpenRootExisting}
                onAssign={handleOpenRootExisting}
              />
            )}

            <JobFormModal
              open={rootFormOpen}
              mode="create"
              initialData={null}
              functions={functions}
              categories={categories}
              statuses={statuses}
              allTasks={allTasks}
              saving={rootFormSaving}
              error={rootFormError}
              onSave={handleRootSave}
              onClose={() => { setRootFormOpen(false); setRootFormError(""); }}
            />
            <ChoiceModal
              open={rootChoiceOpen}
              onNewJob={() => {
                setRootChoiceOpen(false);
                setRootFormOpen(true);
              }}
              onExistingJob={handleOpenRootExisting}
              onClose={() => setRootChoiceOpen(false)}
            />
            <ExistingJobModal
              open={rootExistingOpen}
              parentId={null}
              unassignedJobs={rootUnassignedJobs}
              saving={rootExistingSaving}
              error={rootExistingError}
              onAssign={handleRootAssignExisting}
              onClose={() => { setRootExistingOpen(false); setRootExistingError(""); }}
            />
          </>
        )}

        {/* Screen 2 — hierarchy loaded */}
        {showScreen2 && (
          <OrgCanvas
            key={selectedCompanyId}
            nodes={nodes}
            designatedRootId={designatedRootId}
            onClearDesignatedRoot={clearDesignatedRoot}
            selectedId={selectedNodeId}
            functions={functions}
            categories={categories}
            statuses={statuses}
            allTasks={allTasks}
            companyId={selectedCompanyId}
            onReload={handleReload}
            onSelectNode={setSelectedNodeId}
          />
        )}
      </div>
    </div>
  );
};

export default CLevelOrgChartPage;
